'use client'

import { Avatar, Card, Section, Spinner, Title } from '@/components/atoms'
import { KNOWN_FORM_VALUES } from '@/components/molecules/Form/utils/constants'
import { usePageTracking } from '@/hooks'
import usePopupConfirmRedirect from '@/hooks/usePopupConfirmRedirect'
import { trackPurchase } from '@/utils/gtm'
import { DONATION_PATH_ROOT } from '@/utils/donationPaths'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useMemo, useState } from 'react'
import { usePayStore } from '../../layout'

const { IBAN } = KNOWN_FORM_VALUES

const COPY = {
  default: {
    title: 'Grazie!',
    subTitle: 'Per aver contribuito alla Ricerca Oncologica Italiana.',
    text: 'Con il tuo sostegno ora possiamo aiutare i ricercatori e le ricercatrici a rendere il cancro una malattia sempre più curabile.',
    backHomeCtaLabel: 'Torna in Homepage',
  },
  iban: {
    title: 'Grazie!',
    subTitle: 'La tua donazione è in fase di approvazione',
    text: "Non appena avremo la conferma dall'intestatario dell'IBAN riceverai un'email e procederemo con la donazione.",
    paymentState: 'In stato di approvazione',
    backHomeCtaLabel: 'Torna in Homepage',
  },
}

export default function Page() {
  return (
    <Suspense fallback={<Spinner />}>
      <SuccessoContent />
    </Suspense>
  )
}

function SuccessoContent() {
  const router = useRouter()
  const { store } = usePayStore()
  const searchParams = useSearchParams()
  const [showContent, setShowContent] = useState(false)
  const isPopupWindow = Boolean(
    typeof window !== 'undefined' && window.opener && !window.opener.closed
  )

  // Redirect popup windows that land here by mistake to /confirm
  usePopupConfirmRedirect()

  usePageTracking('donation_success')

  const firstName = useMemo(() => {
    return (
      searchParams.get('firstName') ||
      searchParams.get('companyname') ||
      store?.successBox?.name ||
      store?.formValues?.donatorFirstName ||
      store?.formValues?.donatorCompanyName ||
      ''
    )
  }, [searchParams, store])

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 300)
    return () => clearTimeout(timer)
  }, [])

  const {
    isRecurrentPayment = false,
    successBox = {},
    recurrentPaymentMethod = null,
    formValues = {},
  } = store ?? {}

  const isRecurrentIban = isRecurrentPayment && recurrentPaymentMethod === IBAN

  const hasValidPaymentContext = useMemo(() => {
    if (isPopupWindow) return false
    if (searchParams.get('firstName')) return true
    if (store?.formValues?.donationAmount) return true
    if (store?.successBox?.name) return true
    if (store?.isSingleDonationSuccessful) return true
    if (store?.isRecurrentPayment) return true
    return false
  }, [searchParams, store])

  useEffect(() => {
    if (showContent && !hasValidPaymentContext) {
      router.replace(DONATION_PATH_ROOT)
    }
  }, [showContent, hasValidPaymentContext, router])

  useEffect(() => {
    if (!showContent || !formValues?.donationAmount) return
    handleTracking(formValues)
  }, [showContent, formValues])

  if (!showContent) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <Spinner />
      </div>
    )
  }

  if (!hasValidPaymentContext) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <Spinner />
      </div>
    )
  }

  if (isRecurrentIban) {
    return (
      <IbanSuccessContent
        successBox={successBox}
        onBackHome={() => router.push('/')}
      />
    )
  }

  return (
    <DefaultSuccessContent
      firstName={firstName}
      onBackHome={() => router.push('/')}
    />
  )
}

function handleTracking(formValues) {
  try {
    const {
      donationType,
      donationAmount,
      donationFrequency,
      donatorPaymentMethod,
      donationPaymentMethodOnRecurrenceType,
    } = formValues

    const isMemory = donationType === 'in-memory'
    const isRecurrent = donationFrequency && donationFrequency !== 'one-time'
    const paymentMethod =
      donatorPaymentMethod || donationPaymentMethodOnRecurrenceType

    const getLabels = () => {
      if (isMemory) {
        return {
          name: isRecurrent
            ? 'Donazione continuativa in memoria'
            : 'Donazione in memoria',
          category: isRecurrent
            ? 'donazione generica continuativa in memoria'
            : 'donazione generica memoria',
          variant: isRecurrent ? 'Memoria continuativa' : 'Memoria singola',
        }
      }
      return {
        name: isRecurrent ? 'Donazione continuativa' : 'Erogazione liberale',
        category: isRecurrent
          ? 'donazione generica continuativa'
          : 'donazione generica erogazioni liberali',
        variant: isRecurrent ? 'Libera continuativa' : 'Libera singola',
      }
    }

    const { name, category, variant } = getLabels()
    const paymentTypeLabel =
      paymentMethod === 'card' || paymentMethod === 'creditcard'
        ? 'Card'
        : paymentMethod === 'iban'
          ? 'IBAN'
          : paymentMethod === 'paypal'
            ? 'Paypal'
            : paymentMethod

    trackPurchase({
      payment_type: paymentTypeLabel,
      transaction_id: `#${Date.now()}#PAID`,
      value: donationAmount,
      item_name: name,
      item_id: name,
      price: donationAmount,
      item_category: category,
      item_category2: variant,
      item_variant: variant,
      index: 1,
    })
  } catch (error) {
    console.error('Error tracking purchase:', error)
  }
}

function DefaultSuccessContent({ firstName, onBackHome }) {
  const title = firstName
    ? `Grazie ${firstName.charAt(0).toUpperCase() + firstName.slice(1)}!`
    : 'Grazie!'
  const { subTitle, text, backHomeCtaLabel } = COPY.default

  return (
    <Section className='success-section'>
      <div className='success-section__content'>
        <Avatar
          variant='embedded'
          numberOfAvatar={3}
        />
        <div className='sucess-section__content__head'>
          <Title level={1}>{`<Emph>${title}</Emph>`}</Title>
          <Title level={2}>{subTitle}</Title>
        </div>
        <p className='success-section__content__text'>{text}</p>
      </div>
      <div className='success-section__cta-wrap'>
        <button
          onClick={onBackHome}
          className='success-section__cta-wrap__button'
        >
          {backHomeCtaLabel}
        </button>
      </div>
    </Section>
  )
}

function IbanSuccessContent({ successBox, onBackHome }) {
  const { title, subTitle, text, paymentState, backHomeCtaLabel } = COPY.iban

  const LABELS = {
    name: 'Nome intestatario IBAN',
    lastname: 'Cognome intestatario IBAN',
    email: 'Email intestatario IBAN',
    taxcode: 'Codice fiscale intestatario IBAN',
  }

  const fields = Object.entries(successBox || {})
    .map(([key, value]) => ({
      label: LABELS[key],
      value,
    }))
    .filter((f) => f.label)

  return (
    <Section className='success-section'>
      <div className='success-section__content'>
        <Avatar
          variant='embedded'
          numberOfAvatar={3}
        />
        <div className='sucess-section__content__head'>
          <Title level={1}>{`<Emph>${title}</Emph>`}</Title>
          <Title level={2}>{subTitle}</Title>
        </div>
        <p className='success-section__content__text'>{text}</p>

        {fields.length > 0 && (
          <Card className='success-section__content__card'>
            <p className='success-section__content__card__warn'>
              {paymentState}
            </p>
            <ul className='success-section__content__card__list'>
              {fields.map(({ label, value }) => (
                <li
                  key={label}
                  className='success-section__content__card__list__item'
                >
                  <p className='success-section__content__card__list__item__key'>
                    {label}
                  </p>
                  <p className='success-section__content__card__list__item__value'>
                    {value}
                  </p>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
      <div className='success-section__cta-wrap'>
        <button
          onClick={onBackHome}
          className='success-section__cta-wrap__button'
        >
          {backHomeCtaLabel}
        </button>
      </div>
    </Section>
  )
}
